import { subscriptionData } from "../../types/details-api-types";
import { ILicenses } from "../../types/details-api-types";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import styles from "../../components/AccountSubscriptionCard/AccountSubscriptionCard.module.scss";

// Images
import device from "../../assets/icons/devices.svg";
import devicePause from "../../assets/icons/device-pause.svg";
import deviceCancel from "../../assets/icons/device-cancel.svg";
import settings from "../../assets/icons/settings.svg";

import Button from "../../components/button/Button";
import Modal from "../../components/modal/modal";
import RenewalModal from "../../components/modals/RenewalModal/RenewalModal";
import SuccessfullyRenewalModal from "../../components/modals/SuccessfullyRenewalModal/SuccessfullyRenewalModal";
import ProcessingModal from "../../components/modals/ProcessingModal/ProcessingModal";

interface ExtendedSubscriptionData extends subscriptionData {
  licenses: ILicenses[];
}

export interface PaymentManagementItemProps {
  subNumber: string;
  price: string;
  devicesNumber?: number;
  devicesInUse?: number;
  priceCurrency?: string;
  email: string;
  method: string;
  openModal?: any;
  isExpired?: boolean;
  trial?: boolean;
  setIsModalUpdatePayVisible?: any;
  refreshData: () => void;
  cardData: ExtendedSubscriptionData;
}

const PaymentManagementItem: React.FC<PaymentManagementItemProps> = ({
  trial,
  method,
  email,
  openModal,
  setIsModalUpdatePayVisible,
  cardData,
  refreshData,
  isExpired,
}) => {
  const [isModalRenewalVisible, setIsModalRenewalVisible] = useState(false);
  const [isModalProcessingVisible, setIsModalProcessingVisible] = useState(false);
  const [isModalSuccessfullyVisible, setIsModalSuccessfullyVisible] = useState(false);

  const { t } = useTranslation();
  const capitalize = (str: string): string => str.charAt(0).toLocaleUpperCase() + str.slice(1);

  const renewalStatus: { text: string; img: string } = useMemo(() => {
    if (isExpired) return { text: "Expired", img: deviceCancel };
    else if (cardData.isCanceled) {
      return { text: "Canceled", img: deviceCancel };
    } else if (cardData.isPaused) {
      return { text: "Paused", img: devicePause };
    } else if (cardData.isActive) {
      return { text: "Active", img: device };
    }
    return { text: "Active", img: device };
  }, [cardData.isActive, cardData.isCanceled, cardData.isPaused, isExpired]);

  return (
    <>
      <div className={styles.root}>
        {!trial && (
          <div className={styles.root_history}>
            {/* Payment management */}
            {!trial && (
              <div className={styles.root_wrapper}>
                <h3 className={styles.root_history_title}>{t("Payment Management")}</h3>
                <div className={styles.root_row}>
                  <p className={styles.root_general_payTitle}>{t("Payment Method")}</p>
                  <p className={styles.root_general_card}>
                    {capitalize(method.toLocaleLowerCase() === "card" ? t("Credit Card") : method)}
                  </p>
                </div>
                <h3
                  className={styles.root_general_mobile}
                  onClick={() => {
                    setIsModalUpdatePayVisible(true);
                  }}
                >
                  {t("Change payment method")}
                </h3>
                <div className={styles.root_row}>
                  <p className={styles.root_general_payTitle}>{t("Billing Email:")}</p>
                  <p className={styles.root_general_payInfo}>{email}</p>
                </div>
                <h3 className={styles.root_general_mobile} onClick={openModal}>
                  {t("Change billing email")}
                </h3>

                <div className={styles.root_row}>
                  <p className={styles.root_general_payTitle}>{t("Renewal Status:")}</p>
                  <p className={`${styles.root_general_payInfo} ${renewalStatus.text.toLocaleLowerCase()}`}>
                    {t(renewalStatus.text)}
                  </p>
                </div>

                {(cardData.isCanceled || isExpired) && (
                  <Button
                    onClick={() => window.open("https://secure.fortect.com/", "_blank")}
                    text={t("Renew Plan")}
                    classNames={styles.root_general_renewal}
                  />
                )}

                {cardData.isPaused && !cardData.isCanceled && (
                  <Button
                    onClick={() => setIsModalRenewalVisible(true)}
                    text={t("Reactivate subscription")}
                    classNames={styles.root_general_renewal}
                  />
                )}

                {!cardData.isCanceled && (
                  <div
                    onClick={() => {
                      openModal();
                    }}
                    className={styles.root_row}
                  >
                    <img src={settings} alt="settings" className={styles.root_general_subIcon} />
                    <p className={styles.root_general_sub}>{t("Manage Subscription")}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}

      <Modal isOpen={isModalRenewalVisible} setIsVisible={() => setIsModalRenewalVisible(false)}>
        <RenewalModal
          setIsModalPauseConfirmVisible={setIsModalRenewalVisible}
          setIsModalProcessingVisible={setIsModalProcessingVisible}
          setIsModalSuccessfullyVisible={setIsModalSuccessfullyVisible}
          data={cardData}
        />
      </Modal>

      <Modal isOpen={isModalProcessingVisible} setIsVisible={() => setIsModalProcessingVisible(false)}>
        <ProcessingModal setIsModalProcessingVisible={setIsModalProcessingVisible} />
      </Modal>

      <Modal isOpen={isModalSuccessfullyVisible} setIsVisible={() => setIsModalSuccessfullyVisible(false)}>
        <SuccessfullyRenewalModal
          setIsModalPauseConfirmVisible={() => setIsModalSuccessfullyVisible(false)}
          refreshData={() => refreshData()}
          data={cardData}
        />
      </Modal>
    </>
  );
};

export default PaymentManagementItem;
